// Zone music (Phase 8): one looping track per biome, crossfaded by the same
// biomeFactor scalar that drives the visuals, so the descent is audible.
// Tracks are sourced (CC0 / CC-BY — see public/audio/music/CREDITS.md), not
// synthesized; everything else about the soundscape stays in ambient-audio.
//
// MP3 loop-gap handling: encoders prepend ~1024+ samples of priming silence
// and Chrome/Firefox do NOT trim it in decodeAudioData, which puts an
// audible hiccup at the loop seam. We trim it ourselves: scan for the first
// audible sample and set loopStart there (loopEnd similarly trims the tail).

import { ambientBus, whenAudioReady } from './audio'
import { T } from '../game/tunables'

export interface MusicTrackDef {
  /** Fetched from /audio/music/<file>. */
  file: string
}

interface Voice {
  gain: GainNode
}

let musicGain: GainNode | null = null
const voices = new Map<string, Voice>()

const TAU = 0.6 // crossfade smoothing time-constant (s)

/** First/last index where |sample| exceeds the audibility floor. */
export function audibleRange(data: Float32Array, floor = 1e-4): { start: number; end: number } {
  let start = 0
  while (start < data.length && Math.abs(data[start]) < floor) start++
  let end = data.length
  while (end > start && Math.abs(data[end - 1]) < floor) end--
  return { start, end }
}

async function loadLoop(ctx: AudioContext, out: GainNode, file: string): Promise<Voice | null> {
  try {
    const res = await fetch(`/audio/music/${file}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    // Vite's dev server SPA-falls-back to index.html for missing assets;
    // without this guard decodeAudioData chokes on HTML with a confusing
    // EncodingError (same trap engine/narrator.ts guards against).
    const contentType = res.headers.get('content-type') ?? ''
    if (!contentType.startsWith('audio/')) {
      throw new Error(`missing music file (got content-type: ${contentType || 'unknown'})`)
    }
    const buf = await ctx.decodeAudioData(await res.arrayBuffer())

    // Trim encoder padding using channel 0; sample-accurate loop points.
    const { start, end } = audibleRange(buf.getChannelData(0))
    const src = ctx.createBufferSource()
    src.buffer = buf
    src.loop = true
    src.loopStart = start / buf.sampleRate
    src.loopEnd = end / buf.sampleRate

    const gain = ctx.createGain()
    gain.gain.value = 0
    src.connect(gain)
    gain.connect(out)
    src.start(ctx.currentTime, src.loopStart)
    return { gain }
  } catch (err) {
    console.warn(`[music] failed to load ${file}:`, err)
    return null
  }
}

/** Build the music bus and start all zone loops (muted). Resolves after the
 *  first user gesture; no-op without audio (tests) or when files 404. */
export async function initMusic(tracks: Record<string, MusicTrackDef>): Promise<void> {
  await whenAudioReady()
  const ab = ambientBus()
  if (!ab || musicGain) return
  const { ctx, bus } = ab

  musicGain = ctx.createGain()
  musicGain.gain.value = 1
  musicGain.connect(bus) // rides the ambient bus → narrator duck applies

  for (const [zone, def] of Object.entries(tracks)) {
    const v = await loadLoop(ctx, musicGain, def.file)
    if (v) voices.set(zone, v)
  }
}

/** Per-frame steering: blend 0 = pure 'shallow' voice, 1 = pure 'deep';
 *  danger (0-1) ducks the whole music layer so threats read. */
export function steerMusic(blend: number, danger: number): void {
  const ab = ambientBus()
  if (!ab || !musicGain) return
  const t = ab.ctx.currentTime
  const M = T.audio.music
  const b = Math.max(0, Math.min(1, blend))

  // Equal-power crossfade so the seam doesn't dip.
  const shallow = voices.get('shallow')
  const deep = voices.get('deep')
  if (shallow) shallow.gain.gain.setTargetAtTime(M.level * Math.cos(b * Math.PI / 2), t, TAU)
  if (deep) deep.gain.gain.setTargetAtTime(M.level * Math.sin(b * Math.PI / 2), t, TAU)

  const duck = 1 - M.dangerDuck * Math.max(0, Math.min(1, danger))
  musicGain.gain.setTargetAtTime(duck, t, 0.4)
}
